import React from 'react'
import styled, { css } from 'styled-components'

import { StyledTheme } from 'theme'
import { Button, ButtonProps } from '../button'
import { StyledButton } from '../button/button'
import { MODAL_POPOVER_ID, PopoverRoot } from '../popover/popover-root'

export enum ModalState {
  Opening = 'OPENING',
  Open = 'OPEN',
  Closing = 'CLOSING',
  Closed = 'CLOSED',
}

export const MODAL_CLOSE_DELAY_MS = 150

type ModalSize = keyof StyledTheme['modal']

export interface ModalProps {
  id: string // unique modal id
  isOpen: boolean
  onClose: () => void
  hasOverlay?: boolean
  showCloseButton?: boolean
  closeLabel?: string
  primaryButton?: ButtonProps
  closeOnPrimaryClick?: boolean
  secondaryButton?: ButtonProps
  closeOnSecondaryClick?: boolean
  size?: ModalSize
  hasStickyActionBar?: boolean
  isStackable?: boolean
  isScrollable?: boolean // Use with caution and only with some scrollable modal content.
}

const OpenModalStyle = css`
  background-color: ${({ theme }) => theme.color.blackO50};
`
/* If modal has an overlay (black translucent overlay over the whole page),
  the whole modal is scrollable (not just the content)
*/
const ScrollableModalStyle = css<{ modalState: ModalState }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  justify-content: center;

  overflow: auto;

  transition: background-color ${MODAL_CLOSE_DELAY_MS}ms linear;
  background-color: transparent;
  ${({ modalState }) => modalState === ModalState.Open && OpenModalStyle};
`

const StyledModal = styled.div<{ hasOverlay: boolean; modalState: ModalState }>`
  z-index: ${({ theme }) => theme.zIndex.modal};
  ${({ hasOverlay }) => hasOverlay && ScrollableModalStyle};
`

const OpenNonScrollableContentStyle = css`
  transform: translateY(0px);
  opacity: 1;
`

const NonScrollableContentStyle = css<{ modalState: ModalState }>`
  position: relative;

  transition: transform ${MODAL_CLOSE_DELAY_MS}ms ease-out,
    opacity ${MODAL_CLOSE_DELAY_MS}ms linear;
  transform: translateY(-50px);
  opacity: 0;
  ${({ modalState }) =>
    modalState === ModalState.Open && OpenNonScrollableContentStyle};
`

const OpenScrollableContentStyle = css<{ isStackable: boolean }>`
  transform: translate(
    ${({ isStackable }) => (isStackable ? '0' : '-50%')},
    0px
  );
  opacity: 1;
`

/* If modal has no overlay (like notifications), only the modal content is scrollable.
  In most cases this should not be needed, since notifications etc. should
  have short content.
*/
const ScrollableContentStyle = css<{
  modalState: ModalState
  isStackable: boolean
}>`
  position: fixed;
  top: 0;
  left: 50%;
  max-height: calc(100vh - ${({ theme }) => theme.space.s64});

  transition: transform ${MODAL_CLOSE_DELAY_MS}ms ease-out,
    opacity ${MODAL_CLOSE_DELAY_MS}ms linear;
  transform: translate(
    ${({ isStackable }) => (isStackable ? '0' : '-50%')},
    -50px
  );
  opacity: 0;
  ${({ modalState }) =>
    modalState === ModalState.Open && OpenScrollableContentStyle};
`

/* If modals are stackable, modals are all rendered from top to bottom list (this is possible only for alerts, since there can't be more than one regular modal open)
 * */

const StackableContentStyle = css`
  position: relative;
  left: auto;
  max-width: 100%;

  margin: 0;
  margin-top: ${({ theme }) => theme.space.s16};
`

/* If modals are not scrollable, modals can't be higher than the page (minus modal margins).
Warning: This should be used only in special cases! Without some scrollable element inside the modal (overflow: auto|scroll), some modal content might not be visible to the users on all screen sizes.
 */
const UnScrollableContentStyle = css`
  max-height: calc(100vh - 2 * ${({ theme }) => theme.space.s32});
`

export const ContentWrapper = styled.div<{
  size: ModalSize
  hasOverlay: boolean
  modalState: ModalState
  isStackable: boolean
  isScrollable: boolean
}>`
  ${({ hasOverlay }) => hasOverlay && NonScrollableContentStyle};
  ${({ hasOverlay }) => !hasOverlay && ScrollableContentStyle};

  display: flex;
  flex-direction: column;

  margin: ${({ theme }) => theme.space.s32} 0;

  background-color: ${({ theme }) => theme.color.background};
  border: 1px solid ${({ theme }) => theme.color.grey10};
  border-radius: ${({ theme }) => theme.space.s4};
  box-shadow: ${({ theme }) => theme.shadow.default};

  width: ${({ theme, size }) => theme.modal[size]};
  max-width: 90%;
  height: fit-content;
  z-index: ${({ theme }) => theme.zIndex.modal};

  ${({ isStackable }) => isStackable && StackableContentStyle};
  ${({ isScrollable }) => !isScrollable && UnScrollableContentStyle};

  ${({ theme }) => theme.mediaQuery.phone} {
    min-height: 100%;
    min-width: 100%;
    border-radius: 0;
    margin: 0;
  }
`

const MainContent = styled.div<{ hasOverlay: boolean }>`
  overflow: ${({ hasOverlay }) => (hasOverlay ? 'initial' : 'auto')};

  display: flex;
  flex-direction: column;
  min-height: 0;
  flex-grow: 1;
`

export const ModalChildren = styled.div<{ showCloseButton: boolean }>`
  flex-grow: 1;
  height: fit-content;

  padding-top: ${({ theme }) => theme.space.s32};
  padding-right: ${({ theme, showCloseButton }) =>
    showCloseButton ? theme.space.s48 : theme.space.s24};
  padding-bottom: ${({ theme }) => theme.space.s24};
  padding-left: ${({ theme }) => theme.space.s24};

  display: flex;
  flex-direction: column;
  min-height: 0;

  ${({ theme }) => theme.mediaQuery.phone} {
    margin-top: ${({ theme }) => theme.space.s48};
    padding-left: ${({ theme }) => theme.space.s16};
    padding-right: ${({ theme }) => theme.space.s16};
    justify-content: center;
  }
`

const StickyActionBarStyle = css`
  position: sticky;
  bottom: 0;
`

const ActionBar = styled.div<{ isSticky: boolean }>`
  display: flex;
  justify-content: flex-end;

  background-color: ${({ theme }) => theme.color.grey07};
  padding: ${({ theme }) => theme.space.s8} ${({ theme }) => theme.space.s16};

  ${({ theme }) => theme.mediaQuery.phone} {
    flex-direction: column-reverse;
  }

  ${({ isSticky }) => isSticky && StickyActionBarStyle};
`

const ActionBarButton = styled(Button)`
  &:not(:last-of-type) {
    margin-right: ${({ theme }) => theme.space.s8};
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    &:last-of-type {
      margin-bottom: ${({ theme }) => theme.space.s8};
    }

    ${StyledButton} {
      width: 100%;
    }
  }
`

const CloseButtonWrapper = styled.div`
  margin: ${({ theme }) => theme.space.s12} ${({ theme }) => theme.space.s16};
  position: absolute;
  top: 0;
  right: 0;
`

export const ModalComponent: React.FC<ModalProps & { state: ModalState }> = ({
  id, // separated here, since StyledModal {...props} should not contain this
  onClose,
  size = 'medium',
  primaryButton,
  secondaryButton,
  closeOnPrimaryClick = false,
  closeOnSecondaryClick = true,
  showCloseButton = true,
  hasOverlay = true,
  hasStickyActionBar = false,
  state,
  closeLabel,
  isStackable = false,
  children,
  isScrollable = true,
  ...props
}) => {
  return (
    <StyledModal hasOverlay={hasOverlay} modalState={state} {...props}>
      <ContentWrapper
        size={size}
        hasOverlay={hasOverlay}
        modalState={state}
        isStackable={isStackable}
        isScrollable={isScrollable}
      >
        <MainContent hasOverlay={hasOverlay}>
          <ModalChildren showCloseButton={showCloseButton}>
            {children}
          </ModalChildren>
          {showCloseButton && (
            <CloseButtonWrapper>
              <Button
                variant="basicSecondary"
                icon="clear"
                size="small"
                label={closeLabel}
                onClick={() => onClose()}
              />
            </CloseButtonWrapper>
          )}
        </MainContent>
        {(primaryButton || secondaryButton) && (
          <ActionBar isSticky={hasStickyActionBar}>
            {secondaryButton && (
              <ActionBarButton
                variant="tertiary"
                {...secondaryButton}
                onClick={event => {
                  if (closeOnSecondaryClick) onClose()
                  secondaryButton?.onClick?.(event)
                }}
              />
            )}
            {primaryButton && (
              <ActionBarButton
                variant="primary"
                {...primaryButton}
                onClick={event => {
                  if (closeOnPrimaryClick) onClose()
                  primaryButton?.onClick?.(event)
                }}
              />
            )}
          </ActionBar>
        )}
        <PopoverRoot id={MODAL_POPOVER_ID} />
      </ContentWrapper>
    </StyledModal>
  )
}
